import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  clientDetailDtoSchema,
  type ClientDetailDto,
} from '@lib/dto/client.dto'
import {
  hcpCustomerDtoSchema,
  hcpCustomerListDtoSchema,
  type HcpCustomerDto,
  type HcpCustomerListDto,
} from '@lib/dto/housecall-pro.dto'
import { siteDtoSchema, type SiteDto } from '@lib/dto/site.dto'
import type {
  LinkHcpAddressInput,
  LinkHcpCustomerInput,
} from '@lib/input/housecall-pro.input'
import type { IClientHcpService } from '@/types/ClientHcpService.types'

/**
 * Service for linking clients and sites to Housecall Pro.
 *
 * The two `create*` methods write to HCP's live account. Callers confirm with
 * a person first; nothing here retries, because a retried create can make a
 * second customer.
 */
class ClientHcpService implements IClientHcpService {
  async searchCustomers(
    query: string,
    signal?: AbortSignal,
  ): Promise<HcpCustomerListDto> {
    const params = new URLSearchParams({ q: query }).toString()
    try {
      return await api.get(
        `/housecall-pro/customers?${params}`,
        hcpCustomerListDtoSchema,
        signal === undefined ? undefined : { signal },
      )
    } catch (error) {
      // A search overtaken by the next keystroke is cancelled, not failed.
      if (signal?.aborted === true) throw error
      logger.error('Failed to search Housecall Pro customers', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async getCustomer(id: string): Promise<HcpCustomerDto> {
    try {
      return await api.get(
        `/housecall-pro/customers/${encodeURIComponent(id)}`,
        hcpCustomerDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load a Housecall Pro customer', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async linkCustomer(
    clientId: string,
    customerId: string | null,
  ): Promise<ClientDetailDto> {
    const input: LinkHcpCustomerInput = { customerId }
    try {
      return await api.put(
        `/clients/${encodeURIComponent(clientId)}/housecall-pro`,
        input,
        clientDetailDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to link a client to Housecall Pro', {
        clientId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async createCustomer(clientId: string): Promise<ClientDetailDto> {
    try {
      return await api.post(
        `/clients/${encodeURIComponent(clientId)}/housecall-pro`,
        {},
        clientDetailDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to create a Housecall Pro customer', {
        clientId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async linkAddress(siteId: string, addressId: string | null): Promise<SiteDto> {
    const input: LinkHcpAddressInput = { addressId }
    try {
      return await api.put(
        `/sites/${encodeURIComponent(siteId)}/housecall-pro`,
        input,
        siteDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to link a site to a Housecall Pro address', {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async createAddress(siteId: string): Promise<SiteDto> {
    try {
      return await api.post(
        `/sites/${encodeURIComponent(siteId)}/housecall-pro`,
        {},
        siteDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to create a Housecall Pro address', {
        siteId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const clientHcpService: IClientHcpService = new ClientHcpService()
